import React, { createContext, useContext, useCallback, useRef, useState } from "react";
import type { Variant } from "@/lib/landing/cinematicTransitions";

export interface StartTransitionOptions {
  to: string;
  variant: Variant;
  originPoint?: { x: number; y: number };
  preload?: () => Promise<unknown>;
}

export interface CinematicContextValue {
  startTransition: (opts: StartTransitionOptions) => void;
  isTransitioning: boolean;
  currentOptions: StartTransitionOptions | null;
  onNavigated: () => void;
  onAnimationComplete: () => void;
}

export const CinematicContext = createContext<CinematicContextValue | undefined>(undefined);

export function useCinematicContext(): CinematicContextValue | undefined {
  return useContext(CinematicContext);
}

interface Props {
  children: React.ReactNode;
}

export function CinematicTransitionProvider({ children }: Props) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<StartTransitionOptions | null>(null);
  const navigatedRef = useRef<(() => void) | null>(null);
  const completeRef = useRef<(() => void) | null>(null);

  const startTransition = useCallback((opts: StartTransitionOptions) => {
    setIsTransitioning(true);
    setCurrentOptions(opts);
  }, []);

  const onNavigated = useCallback(() => {
    navigatedRef.current?.();
  }, []);

  const onAnimationComplete = useCallback(() => {
    setIsTransitioning(false);
    setCurrentOptions(null);
    completeRef.current?.();
  }, []);

  return (
    <CinematicContext.Provider
      value={{ startTransition, isTransitioning, currentOptions, onNavigated, onAnimationComplete }}
    >
      {children}
    </CinematicContext.Provider>
  );
}
